import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Shield, Crown, Check, Lock, X, DollarSign, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, ExpressCheckoutElement, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PublicNavbar from "@/components/PublicNavbar";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Inner form that uses ExpressCheckout + PaymentElement
const CheckoutForm = ({ tier, tierInfo }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [expressReady, setExpressReady] = useState(false);

  const confirmPayment = async () => {
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);

    const { error: submitError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/subscription/success?new_user=true`,
      },
    });

    if (submitError) {
      setError(submitError.message);
      setProcessing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await confirmPayment();
  };

  const handleExpressConfirm = async () => {
    await confirmPayment();
  };

  return (
    <div data-testid="payment-element-form">
      {/* Order Summary */}
      <div className="mb-5 pb-4 border-b border-white/10">
        <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Order Summary</p>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white font-semibold">Paladin {tierInfo.name}</p>
            <p className="text-gray-500 text-xs">Recurring {tierInfo.interval}ly subscription</p>
          </div>
          <div className="text-right">
            <p className="text-white text-lg font-bold">{tierInfo.priceDisplay}</p>
            <p className="text-gray-500 text-xs">/{tierInfo.interval}</p>
          </div>
        </div>
      </div>

      {/* Express Checkout: Apple Pay / Google Pay / Link buttons */}
      <div data-testid="express-checkout-container">
        <ExpressCheckoutElement
          onConfirm={handleExpressConfirm}
          onReady={(e) => {
            if (e.availablePaymentMethods) setExpressReady(true);
          }}
          options={{
            layout: { maxColumns: 3, maxRows: 2, overflow: "auto" },
          }}
        />
      </div>

      {/* Divider */}
      {expressReady && (
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-gray-500 uppercase tracking-wider">Or pay with card</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>
      )}

      {/* Card / Payment Element */}
      <form onSubmit={handleSubmit}>
        <div className="mb-5" data-testid="payment-element-container">
          <PaymentElement
            options={{
              layout: { type: "tabs", defaultCollapsed: false },
              wallets: { applePay: "never", googlePay: "never" },
            }}
          />
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm" data-testid="payment-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={!stripe || processing}
          className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-400 text-white font-bold py-4 rounded-xl transition-colors text-base"
          data-testid="submit-payment-btn"
        >
          {processing ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              Processing...
            </span>
          ) : (
            `Subscribe — ${tierInfo.priceDisplay}/${tierInfo.interval}`
          )}
        </button>

        <p className="text-center text-gray-500 text-xs mt-3 flex items-center justify-center gap-1">
          <Lock className="w-3 h-3" /> Secured by Stripe. Cancel anytime.
        </p>
      </form>
    </div>
  );
};

// Wrapper that fetches client_secret and wraps in Elements
const CustomCheckout = ({ tier, tierInfo, couponCode, referralSlug, email }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initCheckout = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API}/payments/create-subscription`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            tier,
            email: email.trim(),
            coupon_code: couponCode || "",
            referral_slug: referralSlug || "",
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Failed to start checkout");
        setClientSecret(data.client_secret);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (email) initCheckout();
  }, [tier, email, couponCode, referralSlug]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        <span className="ml-3 text-gray-400">Loading payment form...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-6">
        <p className="text-red-400 mb-4">{error}</p>
        <button onClick={() => window.location.reload()} className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors">Try Again</button>
      </div>
    );
  }

  if (!clientSecret) return null;

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: "night",
          variables: {
            colorPrimary: "#3B82F6",
            colorBackground: "#0f172a",
            colorText: "#ffffff",
            colorDanger: "#ef4444",
            fontFamily: "system-ui, sans-serif",
            borderRadius: "10px",
            spacingUnit: "4px",
          },
          rules: {
            ".Tab": {
              backgroundColor: "#0f172a",
              border: "1px solid #1E293B",
              color: "#94a3b8",
            },
            ".Tab:hover": {
              backgroundColor: "#1E293B",
              color: "#ffffff",
            },
            ".Tab--selected": {
              backgroundColor: "#1E293B",
              borderColor: "#3B82F6",
              color: "#ffffff",
            },
            ".Input": {
              backgroundColor: "#141B2D",
              borderColor: "#1E293B",
              color: "#ffffff",
            },
            ".Input:focus": {
              borderColor: "#3B82F6",
              boxShadow: "0 0 0 1px rgba(59, 130, 246, 0.3)",
            },
            ".Label": {
              color: "#94a3b8",
            },
          },
        },
      }}
    >
      <CheckoutForm tier={tier} tierInfo={tierInfo} />
    </Elements>
  );
};

const PricingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [consentValid, setConsentValid] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [showInlineCheckout, setShowInlineCheckout] = useState(false);
  const [checkoutEmail, setCheckoutEmail] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [referralSlug, setReferralSlug] = useState("");
  const [referralName, setReferralName] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Handle referral param
    const ref = params.get('ref');
    if (ref) {
      setReferralSlug(ref);
      // Look up affiliate and auto-apply their coupon
      fetch(`${API}/affiliates/lookup?ref=${encodeURIComponent(ref)}`)
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) {
            setPromoCode(data.coupon_code);
            setReferralName(data.name);
            toast.success(`Discount from ${data.name} applied: ${data.discount_display}!`, { duration: 5000 });
          }
        })
        .catch(() => {});
      return;
    }

    // Handle promo/coupon param
    const promoFromUrl = params.get('promo') || params.get('coupon') || params.get('code');
    if (promoFromUrl) {
      setPromoCode(promoFromUrl.toUpperCase());
      toast.info(`Promo code "${promoFromUrl.toUpperCase()}" will be applied at checkout!`, { duration: 4000 });
    }

    // Handle tier pre-selection from landing pages
    const tierParam = params.get('tier');
    if (tierParam && ['foundations', 'accelerator', 'elite'].includes(tierParam)) {
      setSelectedTier(tierParam);
      setShowConsent(true);
    }
  }, [location.search]);

  const tiers = [
    { id: "foundations", name: "Foundations", price: 20, priceDisplay: "$20", interval: "week", features: ["Full workout & program library", "AI workout generator", "Progress tracking & history", "Exercise set tracking with weights", "Plate calculator for lifts"], icon: Zap, popular: false },
    { id: "accelerator", name: "Advanced Accelerator", price: 40, priceDisplay: "$40", interval: "week", features: ["Everything in Foundations", "AI Coach chatbot (24/7)", "In-depth nutritional guidance", "Advanced progress charts", "Priority email support"], icon: Shield, popular: true },
    { id: "elite", name: "Elite Custom Coaching", price: 849, priceDisplay: "$849", interval: "month", features: ["Everything in Accelerator", "AI Form analysis (video/image)", "1-on-1 video coaching sessions", "Personalized nutrition plans", "Direct coach messaging", "VIP priority support"], icon: Crown, popular: false }
  ];

  const handleSubscribe = async (tierId) => {
    if (!showConsent) {
      setSelectedTier(tierId);
      setShowConsent(true);
      setShowInlineCheckout(false);
      setConsentValid(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#070B14] text-white">
      <PublicNavbar />
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          {referralName && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center" data-testid="referral-banner">
              <p className="text-green-400 font-semibold">Referred by {referralName} — discount auto-applied at checkout!</p>
            </motion.div>
          )}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <h1 className="font-bold text-5xl md:text-6xl tracking-tight mb-4">CHOOSE YOUR PATH</h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">Select the plan that matches your fitness ambitions</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {tiers.map((tier, i) => (
              <motion.div key={tier.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 + 0.1 }}
                className={`relative bg-[#0B0F1A] border rounded-2xl p-8 ${tier.popular ? "border-blue-500" : "border-white/10"}`}>
                {tier.popular && (<div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold">Most Popular</div>)}
                <tier.icon className={`w-12 h-12 mb-6 ${tier.popular ? "text-blue-400" : "text-gray-400"}`} />
                <h3 className="text-2xl font-bold mb-2">{tier.name}</h3>
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold">{tier.priceDisplay}</span>
                    <span className="text-gray-400">/{tier.interval}</span>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-3 text-gray-400">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0" />{feature}
                    </li>
                  ))}
                </ul>
                <button onClick={(e) => { e.stopPropagation(); handleSubscribe(tier.id); }} disabled={loading === tier.id}
                  className={`w-full py-3 rounded-full font-semibold transition-all ${tier.popular ? "bg-blue-500 text-white hover:bg-blue-600" : "bg-[#1E293B] text-white hover:bg-[#2D3B4F]"}`}
                  data-testid={`subscribe-${tier.id}`}>
                  {loading === tier.id ? "Loading..." : "Get Started"}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {showConsent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="w-[90%] max-w-6xl bg-[#0A0F1A] border border-white/10 rounded-2xl max-h-[90vh] overflow-y-auto my-8">
            <div className="sticky top-0 bg-[#0A0F1A] border-b border-white/10 p-6 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white">Complete Your Subscription</h2>
                  <p className="text-gray-400 mt-1">
                    {selectedTier && tiers.find(t => t.id === selectedTier)?.name} Plan - {selectedTier && tiers.find(t => t.id === selectedTier)?.priceDisplay}/{selectedTier && tiers.find(t => t.id === selectedTier)?.interval}
                    <span className="ml-2 text-green-400 text-sm">Cancel anytime</span>
                  </p>
                </div>
                <button onClick={() => { setShowConsent(false); setConsentValid(false); setShowInlineCheckout(false); }} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              {/* Email */}
              <div className="mb-5">
                <label className="block text-sm text-gray-400 mb-1.5">Email Address</label>
                <input
                  type="email"
                  value={checkoutEmail}
                  onChange={(e) => setCheckoutEmail(e.target.value)}
                  placeholder="you@email.com"
                  autoFocus
                  className="w-full bg-[#141B2D] border border-[#1E293B] rounded-xl px-4 py-3 text-white text-sm focus:border-blue-500 outline-none transition-all placeholder:text-slate-600"
                  data-testid="checkout-email-input"
                />
              </div>

              {/* Terms Agreement */}
              <div className="mb-5 p-4 bg-[#0B0F1A] border border-white/10 rounded-xl">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={consentValid} onChange={(e) => setConsentValid(e.target.checked)}
                    className="w-5 h-5 mt-0.5 rounded border-[#2D3B4F] bg-[#1E293B] text-blue-500 focus:ring-blue-500" data-testid="terms-checkbox" />
                  <div className="text-gray-300 text-sm leading-relaxed">
                    <span className="font-medium text-white">I agree to the terms and conditions</span>
                    <p className="mt-1 text-gray-400">
                      By checking this box, I confirm I am 18+ years old and agree to the{" "}
                      <a href="/legal/terms" target="_blank" className="text-blue-400 hover:underline">Terms of Service</a>,{" "}
                      <a href="/legal/privacy" target="_blank" className="text-blue-400 hover:underline">Privacy Policy</a>,{" "}
                      <a href="/legal/cancellation" target="_blank" className="text-blue-400 hover:underline">Cancellation Policy</a>, and{" "}
                      <a href="/legal/waiver" target="_blank" className="text-blue-400 hover:underline">Health & Liability Waiver</a>.
                      I authorize recurring billing until cancelled.
                    </p>
                  </div>
                </label>
              </div>

              {/* Payment Form — shown immediately */}
              {checkoutEmail.trim() && checkoutEmail.includes("@") && consentValid ? (
                <div className="bg-[#0B0F1A] border border-white/10 rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-green-400" /><span className="font-semibold text-white">Payment</span></div>
                    <div className="flex items-center gap-2 text-xs text-gray-400"><Lock className="w-4 h-4" /><span>Secured by Stripe</span></div>
                  </div>
                  <div className="p-4">
                    <CustomCheckout
                      tier={selectedTier}
                      tierInfo={tiers.find(t => t.id === selectedTier)}
                      couponCode={promoCode}
                      referralSlug={referralSlug}
                      email={checkoutEmail}
                    />
                  </div>
                </div>
              ) : (
                <div className="bg-[#0B0F1A] border border-white/10 rounded-xl p-6 text-center">
                  <Lock className="w-8 h-8 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">Enter your email and agree to the terms above to see payment options</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
